import { useForm } from "react-hook-form";
import { Button, Form, Modal } from "semantic-ui-react";
import * as yup from "yup";
import { ControlledInput } from "../../../../../../../../components/ControlledForm";
import { useAuthOTPSend } from "../../../../../../../auth/hooks/useAuthOTPSend";
import { OTPModalData } from "./VerifyOTPModal";
import { useToasts } from "react-toast-notifications";
import { Link, useParams } from "react-router-dom";
import { buildReturnUrl } from "../../../../../../../auth/hooks/useAuthIssueAnonymousTokenByOTPInURL";
import { FocusScope } from "react-aria";
import { ToastStatuses } from "../../../../../../../../components/global_components/Toast";

export type ChangeIdentifierModalData = {
  type: "Email" | "Phone";
};

export function ChangeIdentifierModal({
  data,
  onClose,
  onComplete,
}: {
  data: ChangeIdentifierModalData | null;
  onClose: () => void;
  onComplete: (data: OTPModalData) => void;
}) {
  const { site } = useParams<{ site: string }>();

  const { addToast } = useToasts();
  const { sendAuthOTP, isAuthOTPSendLoading } = useAuthOTPSend({
    onSuccess: (data) => {
      onClose();
      onComplete({
        identifier: data.identifier,
        type: data.type,
      });
    },
    onError: (_, message) => {
      addToast(message, {
        appearance: ToastStatuses.Error,
        autoDismiss: true,
      });
    },
  });

  const { control, handleSubmit, errors } = useForm<{
    identifier: string;
  }>({
    resolver: (values) => {
      const errors: { field: keyof typeof values; type: string; message: string }[] = [];

      if (!data) throw new Error("Modal is being used incorrectly, data is null");
      if (data.type === "Email") {
        if (!values.identifier?.trim()) {
          errors.push({
            field: "identifier",
            type: "required",
            message: "Email address is required",
          });
        }

        if (values.identifier && !yup.string().email().isValidSync(values.identifier)) {
          errors.push({
            field: "identifier",
            type: "email",
            message: "Invalid email address",
          });
        }
      }

      if (data.type === "Phone") {
        if (!values.identifier?.trim()) {
          errors.push({
            field: "identifier",
            type: "required",
            message: "Phone number is required",
          });
        }

        if (values.identifier && !isValidPhoneNumber(values.identifier)) {
          errors.push({
            field: "identifier",
            type: "phone",
            message: "Invalid phone number",
          });
        }
      }

      return {
        values,
        errors: errors.reduce((acc, error) => {
          acc[error.field] = { type: error.type, message: error.message };
          return acc;
        }, {} as any),
      };
    },
    defaultValues: {
      identifier: "",
    },
  });

  const headerText = data?.type === "Email" ? "Please Verify Your Email" : "Please Verify Your Phone Number";
  const descriptionText =
    data?.type === "Email"
      ? "You must verify your email address before you will receive any notifications."
      : "You must verify your phone number before you will receive any notifications.";
  const submitButtonText = data?.type === "Email" ? "Send Email" : "Send Text";

  const onSubmit = async ({ identifier }: { identifier: string }) => {
    if (!data) {
      throw new Error("Modal is being used incorrectly, data is null");
    }
    await sendAuthOTP({
      identifier: identifier.trim(),
      type: data.type,
      returnUrl: buildReturnUrl(data.type, identifier.trim()),
    });
    onClose();
  };

  return (
    <Modal open={!!data} onClose={onClose} closeIcon size='tiny'>
      <Modal.Header>{headerText}</Modal.Header>
      <FocusScope contain autoFocus restoreFocus>
        <Modal.Content>
          <p>{descriptionText}</p>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Form.Field required error={!!errors.identifier}>
              <ControlledInput
                control={control}
                errors={errors}
                name='identifier'
                autoComplete={data?.type === "Email" ? "email" : "tel"}
                label={data?.type === "Email" ? "Email Address" : "Phone Number"}
              />
            </Form.Field>
          </Form>
          <p className='tw-mt-3'>
            Already have an account?{" "}
            <Link
              to={{
                pathname: `/${site}/public/login`,
                search: `?redirect=${`/${site}/public/notify/subscription-groups`}`,
              }}
            >
              Sign In
            </Link>
          </p>
        </Modal.Content>
        <Modal.Actions>
          <Button secondary onClick={onClose}>
            Cancel
          </Button>
          <Button
            primary
            onClick={handleSubmit(onSubmit)}
            disabled={isAuthOTPSendLoading}
            loading={isAuthOTPSendLoading}
          >
            {submitButtonText}
          </Button>
        </Modal.Actions>
      </FocusScope>
    </Modal>
  );
}

const phoneRegExp = /^(\+\d{1,2}\s?)?1?\-?\.?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
function isValidPhoneNumber(value: string) {
  return yup.string().matches(phoneRegExp).isValidSync(value);
}
